import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Clock, AlertCircle, RefreshCw, Check } from 'lucide-react';
import axios from 'axios';
import KFSModal from './KFSModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface CaprionStatusCheckProps {
  value?: any;
  onChange?: (data: any) => void;
  onDone?: () => void;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  isBuilderMode?: boolean;
}

interface StatusResponse {
  status: string;
  application_id?: string;
  applicant_id?: string;
  urn?: string;
  orderId?: string;
  loanCode?: string;
  amount?: number;
  productAmount?: number;
  disbursedAmount?: number;
  cashOutFlowAmount?: number;
  disbursementDate?: string;
  tenure?: number;
  roi?: number;
  emi?: number;
  utr?: string;
  last_status_timestamp?: string;
  [key: string]: any;
}

const CaprionStatusCheck: React.FC<CaprionStatusCheckProps> = ({
  value,
  onChange,
  onDone,
  registerSubmit,
  isBuilderMode = false,
}) => {
  const [applicationId, setApplicationId] = useState(value?.application_id || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusData, setStatusData] = useState<StatusResponse | null>(null);
  const [autoChecked, setAutoChecked] = useState(false);
  const [showKFS, setShowKFS] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>('0');
  const [minAmount, setMinAmount] = useState<number>(0);
  const [maxAmount, setMaxAmount] = useState<number>(0);
  const [offerData, setOfferData] = useState<any>(null);
  const [generatingOffer, setGeneratingOffer] = useState(false);
  const [showConfirmButton, setShowConfirmButton] = useState(false);

  useEffect(() => {
    if (value?.application_id) {
      setApplicationId(value.application_id);
    }
  }, [value]);

  // Auto-load application ID from session storage or fetch from backend
  useEffect(() => {
    if (!applicationId && !isBuilderMode) {
      const sessionAppId = sessionStorage.getItem('caprion_application_id');
      const sessionUrn = sessionStorage.getItem('caprion_urn');
      
      if (sessionAppId) {
        console.log('[CaprionStatusCheck] Auto-loaded application ID from session:', sessionAppId);
        setApplicationId(sessionAppId);
      } else if (sessionUrn) {
        console.log('[CaprionStatusCheck] Auto-loaded URN from session:', sessionUrn);
        setApplicationId(sessionUrn);
      } else {
        console.log('[CaprionStatusCheck] No session data, fetching latest application from backend');
        fetchLatestApplication();
      }
    }
  }, [applicationId, isBuilderMode]);

  const fetchLatestApplication = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/caprion/latest-application/`);
      if (response.data.success && response.data.application_id) {
        console.log('[CaprionStatusCheck] Fetched latest application:', response.data.application_id);
        
        sessionStorage.setItem('caprion_application_id', response.data.application_id);
        if (response.data.applicant_id) {
          sessionStorage.setItem('caprion_applicant_id', response.data.applicant_id);
        }
        
        setApplicationId(response.data.application_id);
      }
    } catch (err) {
      console.log('[CaprionStatusCheck] Could not fetch latest application:', err);
    }
  };

  // Auto-check status if application ID is available
  useEffect(() => {
    if (applicationId && !autoChecked && !isBuilderMode && !statusData) {
      console.log('[CaprionStatusCheck] Auto-checking status for:', applicationId);
      setAutoChecked(true);
      checkStatus();
    }
  }, [applicationId, autoChecked, isBuilderMode, statusData]);

  // Generate initial offer when status becomes APPROVED
  useEffect(() => {
    if (statusData?.status?.toLowerCase() === 'approved' && applicationId && !offerData) {
      generateInitialOffer();
    }
  }, [statusData, applicationId, offerData]);

  const generateInitialOffer = async () => {
    try {
      setGeneratingOffer(true);
      console.log('[CaprionStatusCheck] Generating initial offer for:', applicationId);
      
      const response = await axios.post(`${API_BASE_URL}/api/caprion/generate_initial_offer/`, {
        application_id: applicationId,
      });

      if (response.data.success) {
        const offer = response.data.data;
        setOfferData(offer);
        
        // Extract min/max amounts from BRE2 response
        // The offer contains _offer_data_detail_ which has the loan amount info
        const loanAmt = offer._offer_data_detail_?._loan_amt_ || statusData?.amount || 0;
        
        // Set min to 50% of approved amount, max to approved amount
        // In production, these should come from BRE2 response _LoanAmt_Final_.EDI.{6,12}
        const minAmt = Math.floor(loanAmt * 0.5);
        const maxAmt = loanAmt;
        
        setMinAmount(minAmt);
        setMaxAmount(maxAmt);
        setSelectedAmount(loanAmt);
        setInputValue(loanAmt.toString());
        
        console.log('[CaprionStatusCheck] Initial offer generated:', offer);
      }
    } catch (err: any) {
      console.error('[CaprionStatusCheck] Error generating initial offer:', err);
      // Don't show error to user, just log it
    } finally {
      setGeneratingOffer(false);
    }
  };

  // Debounced function to generate offer for selected amount
  const debouncedGenerateOffer = useCallback(
    debounce(async (amount: number) => {
      try {
        console.log('[CaprionStatusCheck] Generating offer for amount:', amount);
        
        const response = await axios.post(`${API_BASE_URL}/api/caprion/generate_offer_for_amount/`, {
          application_id: applicationId,
          amount: amount,
        });

        if (response.data.success) {
          const offer = response.data.data;
          setOfferData(offer);
          console.log('[CaprionStatusCheck] Offer generated for amount:', amount);
        }
      } catch (err: any) {
        console.error('[CaprionStatusCheck] Error generating offer for amount:', err);
      }
    }, 500),
    [applicationId]
  );

  const handleSliderChange = (values: number[]) => {
    const newAmount = values[0];
    setSelectedAmount(newAmount);
    setInputValue(newAmount.toString());
    setShowConfirmButton(false);
    debouncedGenerateOffer(newAmount);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    console.log('[CaprionStatusCheck] Input changed:', value);
    setInputValue(value);
    
    if (value === '') {
      setShowConfirmButton(false);
      return;
    }
    
    const numValue = parseInt(value);
    console.log('[CaprionStatusCheck] Parsed value:', numValue, 'Current:', selectedAmount, 'Range:', minAmount, '-', maxAmount);
    
    // Show confirm button if value is different from current selected amount
    // and within valid range
    if (numValue >= minAmount && numValue <= maxAmount && numValue !== selectedAmount) {
      console.log('[CaprionStatusCheck] Showing confirm button');
      setShowConfirmButton(true);
    } else {
      console.log('[CaprionStatusCheck] Hiding confirm button');
      setShowConfirmButton(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && showConfirmButton) {
      handleConfirmAmount();
    }
  };

  const handleConfirmAmount = () => {
    const numValue = parseInt(inputValue);
    
    if (numValue >= minAmount && numValue <= maxAmount) {
      const clampedValue = Math.max(minAmount, Math.min(maxAmount, numValue));
      setSelectedAmount(clampedValue);
      setInputValue(clampedValue.toString());
      setShowConfirmButton(false);
      
      // Call API with loan_amount parameter
      generateOfferForAmount(clampedValue);
    }
  };

  const generateOfferForAmount = async (amount: number) => {
    try {
      setGeneratingOffer(true);
      console.log('[CaprionStatusCheck] Generating offer for amount:', amount);
      
      const response = await axios.post(`${API_BASE_URL}/api/caprion/generate_offer_for_amount/`, {
        loan_amount: amount,  // Changed from 'amount' to 'loan_amount'
      });

      if (response.data.success) {
        const offer = response.data.data;
        setOfferData(offer);
        console.log('[CaprionStatusCheck] Offer generated for amount:', amount);
      }
    } catch (err: any) {
      console.error('[CaprionStatusCheck] Error generating offer for amount:', err);
    } finally {
      setGeneratingOffer(false);
    }
  };

  const getStatusIcon = (status?: string) => {
    if (!status) return <AlertCircle className="h-6 w-6 text-blue-500" />;
    
    switch (status.toLowerCase()) {
      case 'approved':
      case 'success':
      case 'disbursed':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'rejected':
      case 'failed':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'pending':
      case 'in_progress':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      default:
        return <AlertCircle className="h-6 w-6 text-blue-500" />;
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'bg-blue-100 text-blue-800';
    
    switch (status.toLowerCase()) {
      case 'approved':
      case 'success':
      case 'disbursed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const checkStatus = async () => {
    if (!applicationId.trim()) {
      setError('Please enter an Application ID');
      return;
    }

    setLoading(true);
    setError('');
    setStatusData(null);

    try {
      console.log('[CaprionStatusCheck] Checking status for application:', applicationId);
      
      const response = await axios.post(`${API_BASE_URL}/api/caprion/status/check/`, {
        application_id: applicationId,
      });

      console.log('[CaprionStatusCheck] Response:', response.data);

      if (response.data.success) {
        const statusInfo = response.data.data.status || response.data.data;
        setStatusData(statusInfo);
        
        if (onChange) {
          onChange({
            application_id: applicationId,
            status: statusInfo.status,
            checked_at: new Date().toISOString(),
          });
        }
      } else {
        setError(response.data.message || 'Failed to check status');
      }
    } catch (err: any) {
      console.error('[CaprionStatusCheck] Error:', err);
      
      const errorMessage = err.response?.data?.error ||
                          err.response?.data?.message ||
                          err.message ||
                          'Failed to check application status';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (isBuilderMode) return true;

    if (!statusData) {
      setError('Please check the status first');
      return false;
    }

    if (onDone) {
      onDone();
    }
    return true;
  };

  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(handleSubmit);
    }
  }, [registerSubmit, statusData, isBuilderMode]);

  if (isBuilderMode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Check Application Status</CardTitle>
          <CardDescription>
            Enter your Application ID to check the current status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button disabled>Check Status</Button>
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Preview mode - Status check will be functional in the actual journey
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate amounts - use offerData if available, otherwise statusData
  const loanAmount = offerData?._offer_data_detail_?._loan_amt_ || statusData?.amount || statusData?.productAmount || 0;
  const amountYouGet = offerData?._offer_data_detail_?._cash_out_flow_amt_ || statusData?.disbursedAmount || statusData?.cashOutFlowAmount || 0;
  const deduction = loanAmount - amountYouGet;
  const emi = offerData?._offer_data_detail_?._edi_repayments_ || statusData?.emi || 0;
  const tenure = offerData?._offer_data_detail_?._loan_tenure_ || statusData?.tenure || 0;
  const amountYouPay = emi * tenure;
  const totalInterest = amountYouPay - loanAmount;
  const roi = offerData?._offer_data_detail_?._roi_ || statusData?.roi || 0;

  return (
    <div className="space-y-4">
      {/* Input Card - Only show if no status data yet OR if there was an error */}
      {(!statusData || error) && (
        <Card>
          <CardHeader>
            <CardTitle>Check Application Status</CardTitle>
            <CardDescription>
              {loading ? 'Loading your application status...' : 'Checking your application status...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
                </div>
              )}

              {error && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Display - PetPooja Style */}
      {statusData && (
        <div className="space-y-4">
          {/* Application Status Card with Refresh Button */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(statusData.status)}
                  <div>
                    <div className="text-sm text-gray-500">Application Status</div>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold mt-1 ${getStatusColor(statusData.status)}`}>
                      {statusData.status?.toUpperCase() || 'UNKNOWN'}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setStatusData(null);
                    setAutoChecked(false);
                    checkStatus();
                  }}
                  disabled={loading}
                  variant="outline"
                  size="sm"
                  className="ml-4"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Loan Amount Slider - Only show when APPROVED */}
          {statusData?.status?.toLowerCase() === 'approved' && minAmount > 0 && maxAmount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Loan Amount</CardTitle>
                <CardDescription>
                  Adjust the slider to see different loan offers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Text Input for Direct Amount Entry */}
                  <div className="space-y-2">
                    <Label htmlFor="loan-amount-input" className="text-sm font-medium">
                      Enter Loan Amount
                    </Label>
                    <div className="relative flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                        <Input
                          id="loan-amount-input"
                          type="tel"
                          inputMode="numeric"
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPress}
                          disabled={generatingOffer}
                          className="pl-8 pr-4 text-lg font-semibold"
                          placeholder="Enter amount"
                        />
                      </div>
                      {showConfirmButton && (
                        <Button
                          onClick={handleConfirmAmount}
                          disabled={generatingOffer}
                          size="icon"
                          className="bg-green-600 hover:bg-green-700 h-11 w-11 flex-shrink-0"
                          title="Confirm and generate offer"
                        >
                          <Check className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Range: {formatCurrency(minAmount)} - {formatCurrency(maxAmount)}
                    </p>
                  </div>

                  {/* Slider */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Or Use Slider</Label>
                    <Slider
                      min={minAmount}
                      max={maxAmount}
                      step={1000}
                      value={[selectedAmount]}
                      onValueChange={handleSliderChange}
                      disabled={generatingOffer}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{formatCurrency(minAmount)}</span>
                      <span>{formatCurrency(maxAmount)}</span>
                    </div>
                  </div>

                  {generatingOffer && (
                    <div className="flex items-center justify-center text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Calculating offer...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loan Details Card - PetPooja Style */}
          {loanAmount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-center text-teal-700">Your Final Loan Offer</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Loan Amount */}
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-lg font-semibold">Loan Amount</span>
                    <span className="text-2xl font-bold">{formatCurrency(loanAmount)}</span>
                  </div>

                  {/* Deduction */}
                  {deduction > 0 && (
                    <div className="py-3 border-b">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-lg font-semibold">Deduction</div>
                          <div className="text-sm text-gray-500">ProcFee (incl. GST)</div>
                        </div>
                        <span className="text-xl font-bold text-red-600">- {formatCurrency(deduction)}</span>
                      </div>
                    </div>
                  )}

                  {/* Amount You Get */}
                  {amountYouGet > 0 && (
                    <div className="py-3 border-b bg-green-50 -mx-6 px-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-lg font-semibold text-green-900">Amount You Get</div>
                          <div className="text-sm text-green-700">Loan Amount - Deduction</div>
                        </div>
                        <span className="text-2xl font-bold text-green-900">{formatCurrency(amountYouGet)}</span>
                      </div>
                    </div>
                  )}

                  {/* Amount You Pay */}
                  {amountYouPay > 0 && (
                    <div className="py-3 border-b">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-lg font-semibold">Amount You Pay</div>
                          <div className="text-sm text-gray-500">@{roi}% per month</div>
                        </div>
                        <span className="text-2xl font-bold">{formatCurrency(amountYouPay)}</span>
                      </div>
                    </div>
                  )}

                  {/* Total Interest Payable */}
                  {totalInterest > 0 && (
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-lg font-semibold">Total Interest Payable</span>
                      <span className="text-xl font-bold">{formatCurrency(totalInterest)}</span>
                    </div>
                  )}

                  {/* Monthly EMI */}
                  {emi > 0 && (
                    <div className="flex justify-between items-center py-3 border-b">
                      <span className="text-lg font-semibold">Monthly Installment</span>
                      <span className="text-xl font-bold">{formatCurrency(emi)}</span>
                    </div>
                  )}

                  {/* Loan Tenure */}
                  {tenure > 0 && (
                    <div className="flex justify-between items-center py-3">
                      <span className="text-lg font-semibold">Loan Tenure</span>
                      <span className="text-xl font-bold">
                        {(() => {
                          const repaymentFrequency = statusData?.repayment_frequency;
                          
                          // Database stores tenure in DAYS
                          // Convert to appropriate unit based on repayment frequency
                          if (repaymentFrequency === 'EDI') {
                            // Daily - show in days
                            return `${tenure} ${tenure === 1 ? 'Day' : 'Days'}`;
                          } else if (repaymentFrequency === 'EWI') {
                            // Weekly - convert days to weeks
                            const weeks = Math.round(tenure / 7);
                            return `${weeks} ${weeks === 1 ? 'Week' : 'Weeks'}`;
                          } else if (repaymentFrequency === 'EMI') {
                            // Monthly - convert days to months
                            const months = Math.round(tenure / 30);
                            return `${months} ${months === 1 ? 'Month' : 'Months'}`;
                          } else {
                            // Fallback - show days
                            return `${tenure} ${tenure === 1 ? 'Day' : 'Days'}`;
                          }
                        })()}
                      </span>
                    </div>
                  )}

                  {/* View KFS Button - Only show when APPROVED and offer is generated */}
                  {statusData?.status?.toLowerCase() === 'approved' && offerData && (
                    <div className="pt-4 border-t">
                      <Button
                        onClick={() => setShowKFS(true)}
                        className="w-full bg-intellend-green hover:bg-intellend-green/90"
                        size="lg"
                      >
                        View Key Fact Statement
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* KFS Modal */}
      <KFSModal 
        isOpen={showKFS} 
        onClose={() => setShowKFS(false)}
        applicationId={applicationId}
      />
    </div>
  );
};

export default CaprionStatusCheck;
